/* ==========================================================================
   InputPanel > Client > panel
   ========================================================================== */

/* Modules */
const { fromEvent, of } = require('rxjs');
const { switchMap, mergeAll, filter, skip } = require('rxjs/operators');

const { toNode } = require('@bluekiri/alexandria/helpers/domHelpers');
const { randomId } = require('@bluekiri/alexandria/helpers/dataHelpers');

const TooltipClient = require('@bluekiri/alexandria-contrib-components/components/tooltip/client/js');

const { actions, types } = require('../store/actions');

const TooltipTemplate = require('../../../templates/tooltipPanel');

const configuration = require('../config');

function addContent(instance, content) {
    const { store = {}, hooks } = instance;
    const { panel } = store.select('data');

    /* Remove current content */
    removeContent(instance);

    if (!content) return;

    if (panel)
        /* Append Content */
        [...toNode(content)].forEach(node => hooks.panelContent.appendChild(node));
}

function removeContent(instance) {
    const { store = {}, hooks = {} } = instance;
    const { panel } = store.select('data');

    if (panel && hooks.panelContent)
        /* Remove nodes */
        while (hooks.panelContent.firstChild) {
            hooks.panelContent.removeChild(hooks.panelContent.lastChild);
        }
}

function createPanel(instance) {
    const { config = {}, store = {} } = instance;
    const { context = {}, placement, panelStyle } = config;

    const styles = Array.isArray(panelStyle) ? panelStyle.join(' ') : panelStyle;

    /* Data */
    const { panelType } = store.select('data');

    /* Prepare tooltip data */
    const dataTooltip = {
        placement,
        type: panelType,
        panelStyle: styles
    };

    return toNode(TooltipTemplate(dataTooltip, context))[0];
}

function addPanel(instance) {
    const result = instance;
    const { config = {}, store = {}, element } = instance;
    const { appendToParent } = config;

    /* Data */
    const { panelType } = store.select('data');

    const appendTo = appendToParent ? element : document.body;

    /* Append tooltip panel */
    appendTo.appendChild(createPanel(instance));

    /* Init tooltip */
    result.panel = TooltipClient(`[data-component="Tooltip"][data-type="${panelType}"]`, {
        trigger: instance.input.element
    });

    if (result.panel) {
        /* Extends Events */
        result.events = {
            ...result.events,
            click$: fromEvent(window.document, 'click').pipe(
                skip(1),
                filter(({ target }) => !target.closest(`[data-type="${panelType}"]`))
            )
        };

        /* Panel Close events */
        const close$ = of(instance.events.keyup$, instance.events.click$).pipe(
            mergeAll(),
            switchMap(event => of(event))
        );

        /* Extends hooks */
        result.hooks = {
            ...result.hooks,
            panelContent: result.panel.element.querySelector(`[${configuration().hooks.panelContent}]`)
        };

        /* Extend reactions */
        result.reactions = {
            ...result.reactions,
            removeContent: result.store.subscribe((state, action) => {
                if (action.type !== types.REMOVE_CONTENT) return;

                removeContent(instance);
            }),
            setContent: result.store.subscribe((state, action) => {
                if (action.type !== types.SET_CONTENT) return;

                addContent(instance, action.payload);
            })
        };

        /* Extends subscriptions */
        result.subscriptions = {
            ...result.subscriptions,
            closePanel: close$.subscribe(() => {
                instance.actions.closePanel();
            })
        };
    }
}

function initPanel(instance) {
    const result = instance;

    /* Extends Actions */
    result.actions = {
        ...result.actions,
        openPanel: () => {
            const type = randomId(randomId(configuration().type.panel));
            return result.store.dispatch(actions.openPanel(type));
        },
        closePanel: () => result.store.dispatch(actions.closePanel()),
        setContent: content => result.store.dispatch(actions.setContent(content))
    };

    /* Extends reactions */
    result.reactions = {
        ...result.reactions,
        openPanel: instance.store.subscribe((state, action) => {
            if (action.type !== types.OPEN_PANEL) return;
            const { active } = result.input.store.select('input');

            if (!active) result.input.actions.toggleActive();

            addPanel(instance);
        }),
        closePanel: result.store.subscribe((state, action) => {
            if (action.type !== types.CLOSE_PANEL) return;

            /* Data */
            const { active, value } = result.input.store.select('input');

            if (active && (value === null || value === '')) result.input.actions.toggleActive();

            destroyPanel(result);
        })
    };
}

function destroyPanel(instance) {
    const result = instance;

    if (!instance.panel) return;

    /* Unsubscribe */
    instance.panel.actions.destroy();
    instance.subscriptions.closePanel.unsubscribe();

    /* Remove references */
    delete result.hooks.panelContent;
    delete result.subscriptions.closePanel;
    delete result.panel;
}

module.exports = {
    addPanel,
    initPanel,
    destroyPanel
};
